/**
 * Hook agregador de notificacoes acionaveis (Bloco UI / UI.2).
 *
 * Conta em paralelo, filtrado por permissao do usuario:
 *   - WhatsApp: nao lidas (reusa useWhatsappNaoLidasGlobal, ja em polling 10s)
 *   - Assistencias atrasadas: GET /api/assistencias?atrasadas=true
 *   - Contratos pendentes assinatura: GET /api/contratos?situacao=PENDENTE_ASSINATURA
 *   - OSEs atrasadas: GET /api/oses?atrasadas=true
 *
 * Cada fetch usa `silentError: true` — falha de um nao derruba os outros.
 *
 * Polling 60s (pausa em document.hidden) via useWhatsappPolling. WhatsApp
 * tem seu proprio polling 10s separado (mais responsivo); aqui agregamos
 * apenas a contagem ja conhecida.
 *
 * Cada fonte retorna o numero de itens pendentes + URL de drill-down +
 * label legivel + permissao requerida (para o dropdown filtrar).
 */

import { lerResumoAreaPcp } from "../services/areaPcp";
import { lerResumoVendasAssinadas } from "../services/capacidade";
import { EVENTO_NAO_LIDAS_DO_CHAT, conversasComNaoLidas } from "../services/notificacoesDoChat";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { MODULO_CENTRAL_PROJETOS, MODULO_PLANILHA_LEGADO } from "../config/modulos";
import { useAuth } from "../context/AuthContext";
import { lerResumoDosAvisos } from "../services/avisos";
import { useDados } from "../context/DadosContext";
import { foraDeOrdem } from "../utils/fluxoExecutivo";
import {
  useWhatsappNaoLidasGlobal,
} from "./useWhatsappNaoLidas";
import useWhatsappPolling from "./useWhatsappPolling";


const POLL_INTERVAL_MS = 60000;

// Solicitações internas (PM-07): UMA chamada (`/pendencias-resumo`) traz as
// filas; o backend já filtra pelo que a pessoa resolve e pela unidade dela.
// Coletor irmão `_coletar_solicitacoes_internas` em tarefas/services.py —
// sino e Minhas Pendências contam a mesma coisa.
const FILAS_SOLICITACOES = [
  { id: "aguardando_autorizacao", url: "/suprimentos/gestao-solicitacoes" },
  { id: "pecas_sem_lote", url: "/minhas-pendencias" },
  { id: "sem_separacao", url: "/minhas-pendencias" },
  { id: "reservas_presas", url: "/minhas-pendencias" },
  { id: "causa_pendente", url: "/minhas-pendencias" },
  { id: "mencoes", url: "/minhas-pendencias" },
  { id: "novidades", url: "/suprimentos/solicitacoes" },
];
const CODIGOS_VER_SOLICITACAO = [
  "solicitacao:view", "solicitacao_asm:view", "solicitacao_ast:view",
  "solicitacao_asp:view", "solicitacao_rqa:view", "solicitacao_rqi:view",
];


function _safeCount(payload) {
  if (!payload) return 0;
  if (typeof payload.count === "number") return payload.count;
  if (Array.isArray(payload)) return payload.length;
  if (Array.isArray(payload.results)) {
    // Quando ha pagination, prefere `count` total; senao, length da pagina atual.
    return typeof payload.count === "number"
      ? payload.count
      : payload.results.length;
  }
  return 0;
}


const NotificacoesContext = createContext(null);


function _useNotificacoesAgregadasInterno() {
  const { request, hasPermission, authenticated, usuario, temModulo } = useAuth();
  // Fontes de módulo vendido à parte (ADR-0102): sem o módulo, a rota dá 404
  // e o item levaria a tela que não existe — nem pergunta ao servidor.
  const comPlanilha = Boolean(authenticated) && Boolean(temModulo?.(MODULO_PLANILHA_LEGADO));
  const comCentral = Boolean(authenticated) && Boolean(temModulo?.(MODULO_CENTRAL_PROJETOS));
  const whatsapp = useWhatsappNaoLidasGlobal();

  const podeAssistencia = Boolean(authenticated) && hasPermission("assistencia:view");
  const podeContrato = Boolean(authenticated) && hasPermission("contrato:view");
  const podeOse = Boolean(authenticated) && hasPermission("terceirizacao:view");
  // Revisao de orcamento: conta SO os do vendedor logado (quem criou/enviou).
  const meuId = usuario?.id || null;
  const podeRevisaoOrc =
    Boolean(authenticated) && hasPermission("orcamento:view") && Boolean(meuId);
  // Bloco 7.1: o sino cobria 5 fontes e o resto do sistema avisava por
  // conta propria (ou nao avisava). Estas sao as que faltavam — cada uma
  // tem o coletor irmao em `tarefas/services.py`, para o sino e Minhas
  // Pendencias contarem a mesma coisa.
  const podeLead = Boolean(authenticated) && hasPermission("lead:view");
  const podeFiscal = Boolean(authenticated) && hasPermission("fiscal:view");
  const podeCobranca = Boolean(authenticated) && hasPermission("cobranca:view");
  // Plano de Suprimentos (2026-09): as 4 filas novas do almoxarifado, com o
  // coletor irmao em `tarefas/services.py` (sino e Minhas Pendencias contam
  // a mesma coisa). Cada uma gateada pela permissao de quem RESOLVE.
  const podeAprovarAjuste = Boolean(authenticated) && hasPermission("estoque:aprovar");
  const podeAprovarInventario = Boolean(authenticated) && hasPermission("inventario:aprovar");
  const podeCadastrarSolicitado = Boolean(authenticated) && hasPermission("solicitacao_cadastro:aprovar");
  const podeConfirmarSobra = Boolean(authenticated) && hasPermission("sobra:change_status");
  // Fila do comprador (ADR-0069): requisicao com prazo de cotacao vencido —
  // coletor irmao `_coletar_requisicoes_atrasadas` em tarefas/services.py.
  const podeCotar = Boolean(authenticated) && hasPermission("compras:aprovar_requisicao");
  // Formularios (FORM-13): resposta que o CRM recusou (FALHOU) e formulario
  // ativo com envio recusado nas ultimas 24 h. Coletores irmaos
  // `_coletar_respostas_de_formulario_falhas` e `_coletar_recusas_de_formulario`
  // em tarefas/services.py; o endpoint /pendencias soma os dois em `count`.
  const podeGerirFormularios = Boolean(authenticated) && hasPermission("formulario:manage");
  const podeSolicitacoes =
    Boolean(authenticated) && CODIGOS_VER_SOLICITACAO.some((c) => hasPermission(c));
  // Mapa da Producao (Legado): tarefa/peca atrasada e tarefa pausada — coletor
  // irmao `_coletar_mapa_producao` em tarefas/services.py.
  const podeOperarMapa = comPlanilha && hasPermission("mapa_producao:operar");
  // Projetista do executivo (Legado): ambientes que outra pessoa passou para
  // ele nos últimos dias. Aviso, não fila: some sozinho depois de uma semana.
  const podeSerProjetista = comPlanilha && hasPermission("executivo:ser_projetista");
  // Capacidade da Fábrica (Legado): venda assinada que o PCP ainda não ligou
  // ao pedido da planilha — coletor irmão `_coletar_vendas_assinadas` em
  // tarefas/services.py (mesma regra de `capacidade.pendencias`). Exige `:view`
  // também: o item leva a /legado/capacidade, que só abre com ela.
  const podeLigarVenda =
    comPlanilha &&
    hasPermission("capacidade_fabrica:view") &&
    hasPermission("capacidade_fabrica:ligar_venda");
  // Área do PCP (ADR-0087): execução parada há mais de 1 dia útil — coletor
  // irmão `_coletar_area_pcp` em tarefas/services.py (mesma regra de
  // `area_pcp.pendencias`). Exige `:view` também: o item leva à execução.
  const podeExecutarPcp =
    comPlanilha && hasPermission("area_pcp:view") && hasPermission("area_pcp:executar");
  // Chat Pharu (ADR-0077): mensagens não lidas — o backend já filtra por participação.
  const podeChat = Boolean(authenticated) && hasPermission("chat:view");
  // Executivo na ordem nova com APROVAÇÃO digitada antes da REVISÃO. Sai da
  // planilha já carregada (sem pedido ao servidor) e leva à fila de Revisão,
  // onde a linha aparece com o selo "Fora de ordem".
  const podeRevisar = comPlanilha && hasPermission("revisao:view");
  // Central de Projetos (ADR-0089): pendências que esperam a marcenaria
  // (quem trata), conversas com alerta Projetos aberto (quem vê a Central) e o
  // monitor do WhatsApp parado ou pedindo QR (quem configura).
  const podeVerCentral = comCentral && hasPermission("central_projetos:view");
  const { dados: linhasDaPlanilha } = useDados() || {};
  const foraDeOrdemExecutivo = useMemo(
    () => (podeRevisar && Array.isArray(linhasDaPlanilha) ? linhasDaPlanilha.filter(foraDeOrdem).length : 0),
    [podeRevisar, linhasDaPlanilha],
  );

  const [estado, setEstado] = useState({
    assistencias: 0,
    contratos: 0,
    oses: 0,
    revisoesOrcamento: 0,
    followups: 0,
    vendasSemGanho: 0,
    leadsSemContato: 0,
    tarefasLead: 0,
    compromissosLead: 0,
    nfErro: 0,
    cobrancasVencidas: 0,
    ajustesAguardando: 0,
    inventariosAguardando: 0,
    solicitacoesCadastro: 0,
    sobrasPrevistas: 0,
    requisicoesAtrasadas: 0,
    formulariosPendentes: 0,
    mapaProducao: 0,
    executivoRecebidos: 0,
    centralPendencias: 0,
    centralAlertaProjetos: 0,
    centralMonitorParado: 0,
    vendasAssinadas: 0,
    areaPcpParadas: 0,
    chatNaoLidas: 0,
    chatMencoes: 0,
    solicitacoes: { total: 0, rotulos: {} },
    // Agenda v2 (Fase 3): avisos pessoais (compartilhada, comentário,
    // subtarefa, lembrete). Ninguém precisa de permissão: são da pessoa.
    avisosAgenda: 0,
    ultimoAviso: null,
    atualizadoEm: null,
  });

  const carregar = useCallback(async () => {
    if (!authenticated) return;
    const tarefas = [];

    if (podeAssistencia) {
      tarefas.push(
        request("/api/assistencias?atrasadas=true&page_size=1", { silentError: true })
          .then((p) => ({ assistencias: _safeCount(p) }))
          .catch(() => ({}))
      );
    }
    if (podeContrato) {
      tarefas.push(
        request("/api/contratos?situacao=PENDENTE_ASSINATURA&page_size=1", { silentError: true })
          .then((p) => ({ contratos: _safeCount(p) }))
          .catch(() => ({}))
      );
    }
    if (podeOse) {
      tarefas.push(
        request("/api/oses?atrasadas=true&page_size=1", { silentError: true })
          .then((p) => ({ oses: _safeCount(p) }))
          .catch(() => ({}))
      );
    }
    if (podeRevisaoOrc) {
      tarefas.push(
        request(
          `/api/orcamentos?revisao_solicitada=true&vendedor=${meuId}&page_size=1`,
          { silentError: true },
        )
          .then((p) => ({ revisoesOrcamento: _safeCount(p) }))
          .catch(() => ({}))
      );
    }

    if (podeLead) {
      tarefas.push(
        // Só a própria carteira (meus=1): o coletor irmão no backend também
        // filtra por responsável — gestor não recebe follow-up dos vendedores.
        request("/api/crm/leads?followup_vencido=true&meus=1&page_size=1", { silentError: true })
          .then((p) => ({ followups: _safeCount(p) }))
          .catch(() => ({}))
      );
      // CRM-04: pedido criado com o lead ainda aberto no CRM (a sincronização
      // orçamento -> lead falhou). Coletor irmão:
      // tarefas/services._coletar_vendas_sem_ganho_no_crm — mesma fonte e
      // mesmo recorte (só o responsável).
      tarefas.push(
        request("/api/crm/leads?venda_sem_ganho=true&meus=1&page_size=1", { silentError: true })
          .then((p) => ({ vendasSemGanho: _safeCount(p) }))
          .catch(() => ({}))
      );
      // CRM-25 / FORM-06: lead novo sem contato (do responsável ou, sem ele,
      // de quem pode atribuir), tarefa vencida e compromisso das próximas
      // 24 h. Um endpoint só, com a mesma régua dos coletores irmãos
      // `_coletar_leads_novos_sem_contato`, `_coletar_tarefas_lead` e
      // `_coletar_compromissos_lead` em tarefas/services.py.
      tarefas.push(
        request("/api/crm/pendencias", { silentError: true })
          .then((p) => ({
            leadsSemContato: Number(p?.leads_sem_contato) || 0,
            tarefasLead: Number(p?.tarefas_vencidas) || 0,
            compromissosLead: Number(p?.compromissos_proximos) || 0,
          }))
          .catch(() => ({}))
      );
    }
    if (podeFiscal) {
      tarefas.push(
        request("/api/notas-fiscais?situacao=ERRO&page_size=1", { silentError: true })
          .then((p) => ({ nfErro: _safeCount(p) }))
          .catch(() => ({}))
      );
    }
    if (podeCobranca) {
      // `situacao=VENCIDO` seria um filtro vazio: nada no sistema atribui
      // esse estado (M-FIS-3). O que existe de verdade e pendente com
      // vencimento no passado.
      tarefas.push(
        request("/api/boletos?vencidas=true&page_size=1", { silentError: true })
          .then((p) => ({ cobrancasVencidas: _safeCount(p) }))
          .catch(() => ({}))
      );
    }
    if (podeAprovarAjuste) {
      tarefas.push(
        request("/api/ajustes-pendentes?estado=AGUARDANDO&page_size=1", { silentError: true })
          .then((p) => ({ ajustesAguardando: _safeCount(p) }))
          .catch(() => ({}))
      );
    }
    if (podeAprovarInventario) {
      tarefas.push(
        request("/api/inventarios?estado=AGUARDANDO_APROVACAO&page_size=1", { silentError: true })
          .then((p) => ({ inventariosAguardando: _safeCount(p) }))
          .catch(() => ({}))
      );
    }
    if (podeCadastrarSolicitado) {
      tarefas.push(
        request("/api/solicitacoes-cadastro?estado=PENDENTE,EM_ANALISE&page_size=1", { silentError: true })
          .then((p) => ({ solicitacoesCadastro: _safeCount(p) }))
          .catch(() => ({}))
      );
    }
    if (podeConfirmarSobra) {
      tarefas.push(
        request("/api/sobras?estado=prevista&page_size=1", { silentError: true })
          .then((p) => ({ sobrasPrevistas: _safeCount(p) }))
          .catch(() => ({}))
      );
    }

    if (podeCotar) {
      tarefas.push(
        request("/api/requisicoes?atrasadas=1", { silentError: true })
          .then((p) => ({ requisicoesAtrasadas: _safeCount(p) }))
          .catch(() => ({}))
      );
    }
    if (podeGerirFormularios) {
      tarefas.push(
        request("/api/formularios/pendencias", { silentError: true })
          .then((p) => ({ formulariosPendentes: _safeCount(p) }))
          .catch(() => ({}))
      );
    }

    if (podeOperarMapa) {
      tarefas.push(
        request("/api/mapa-producao/pendencias-resumo", { silentError: true })
          .then((p) => (p && typeof p.total === "number" ? { mapaProducao: p.total } : {}))
          .catch(() => ({}))
      );
    }

    if (podeVerCentral) {
      tarefas.push(
        request("/api/central-projetos/sino", { silentError: true })
          .then((p) => ({
            centralPendencias: typeof p?.pendencias === "number" ? p.pendencias : 0,
            centralAlertaProjetos: typeof p?.alerta_projetos === "number" ? p.alerta_projetos : 0,
            centralMonitorParado: p?.monitor_parado ? 1 : 0,
          }))
          .catch(() => ({}))
      );
    }

    if (podeSerProjetista) {
      tarefas.push(
        request("/api/mapa-producao/executivo/projetistas/recebidos", { silentError: true })
          .then((p) => (p && typeof p.total === "number" ? { executivoRecebidos: p.total } : {}))
          .catch(() => ({}))
      );
    }

    if (podeLigarVenda) {
      tarefas.push(
        lerResumoVendasAssinadas(request)
          .then((p) => (p && typeof p.total === "number" ? { vendasAssinadas: p.total } : {}))
          .catch(() => ({}))
      );
    }

    if (podeExecutarPcp) {
      tarefas.push(
        lerResumoAreaPcp(request)
          .then((p) => (p && typeof p.total === "number" ? { areaPcpParadas: p.total } : {}))
          .catch(() => ({}))
      );
    }

    if (podeChat) {
      tarefas.push(
        request("/api/chat/nao-lidas", { silentError: true })
          // Quantas CONVERSAS têm mensagem não lida, não quantas mensagens
          // (decisão da dona, 2026-09-29): o mesmo número do menu do PharuCut.
          .then((p) => ({ chatNaoLidas: conversasComNaoLidas(p?.data?.por_conversa ?? p?.por_conversa) }))
          .catch(() => ({}))
      );
      // Fase 2: quem te citou (coletor irmão `_coletar_mencoes_chat` em tarefas/services.py).
      tarefas.push(
        request("/api/chat/mencoes", { silentError: true })
          .then((p) => ({ chatMencoes: Number(p?.data?.total ?? p?.total) || 0 }))
          .catch(() => ({}))
      );
    }

    if (podeSolicitacoes) {
      tarefas.push(
        request("/api/solicitacoes/pendencias-resumo", { silentError: true })
          .then((p) => (p && typeof p.total === "number" ? { solicitacoes: p } : {}))
          .catch(() => ({}))
      );
    }

    tarefas.push(
      lerResumoDosAvisos(request)
        .then((p) => (p && typeof p.nao_lidas === "number" ? { avisosAgenda: p.nao_lidas, ultimoAviso: p.ultima || null } : {}))
        .catch(() => ({}))
    );

    const resultados = await Promise.all(tarefas);
    const merge = resultados.reduce((acc, r) => ({ ...acc, ...r }), {});
    // Espalhar `prev` primeiro é o que garante que TODA fonte sobreviva ao
    // ciclo. Enumerar chave a chave, como estava, faz a fonte nova sumir do
    // estado no primeiro fetch: ela vira `undefined`, entra na soma do
    // total, e o total inteiro vira NaN. Como o badge só aparece com
    // `total > 0` — e `NaN > 0` é falso — o sino apaga por completo e o
    // menu passa a dizer "tudo em dia". Nenhum erro em lugar nenhum.
    //
    // Fetch que falhou devolve `{}`, então a chave nem entra no `merge` e o
    // valor anterior continua valendo — um backend instável não zera
    // contador.
    setEstado((prev) => ({
      ...prev,
      ...merge,
      atualizadoEm: new Date(),
    }));
  }, [authenticated, podeAssistencia, podeContrato, podeOse, podeRevisaoOrc,
    podeLead, podeFiscal, podeCobranca, podeAprovarAjuste,
    podeAprovarInventario, podeCadastrarSolicitado, podeConfirmarSobra, podeCotar,
    podeGerirFormularios, podeSolicitacoes, podeOperarMapa, podeSerProjetista, podeLigarVenda, podeExecutarPcp, podeChat,
    podeVerCentral,
    meuId, request]);

  // Bloco LOG2 Camada D (2026-05-25): atrasa 3s o primeiro fetch para
  // nao concorrer com a cascata pos-login (loadMe + permissoes +
  // widgets + sino + WhatsApp = 10 requests simultaneas). Backend
  // Render Free agradece a folga. Polling 60s continua normal apos.
  useWhatsappPolling(
    carregar,
    authenticated ? POLL_INTERVAL_MS : 0,
    [authenticated, podeAssistencia, podeContrato, podeOse, podeRevisaoOrc, meuId],
    { delayPrimeiroMs: 3000 },
  );

  // Com a página do Chat aberta, o contador vem dela, na hora (o stream já
  // trouxe): antes ler uma conversa levava até 60 s para apagar o sino.
  useEffect(() => {
    if (!podeChat) return undefined;
    const aoMudar = (e) => {
      const conversas = Number(e.detail?.conversas);
      if (Number.isFinite(conversas)) setEstado((prev) => ({ ...prev, chatNaoLidas: conversas }));
    };
    window.addEventListener(EVENTO_NAO_LIDAS_DO_CHAT, aoMudar);
    return () => window.removeEventListener(EVENTO_NAO_LIDAS_DO_CHAT, aoMudar);
  }, [podeChat]);

  const total =
    (whatsapp?.total || 0) + estado.assistencias + estado.contratos
    + estado.oses + estado.revisoesOrcamento
    + estado.followups + estado.vendasSemGanho + estado.nfErro
    + estado.leadsSemContato + estado.tarefasLead + estado.compromissosLead
    + estado.cobrancasVencidas
    + estado.ajustesAguardando + estado.inventariosAguardando
    + estado.solicitacoesCadastro + estado.sobrasPrevistas
    + estado.requisicoesAtrasadas
    + estado.formulariosPendentes
    + estado.mapaProducao
    + estado.executivoRecebidos
    + (estado.centralPendencias || 0)
    + (estado.centralAlertaProjetos || 0)
    + (estado.centralMonitorParado || 0)
    + foraDeOrdemExecutivo
    + (estado.vendasAssinadas || 0)
    + (estado.areaPcpParadas || 0)
    + (estado.solicitacoes?.total || 0)
    + (estado.chatNaoLidas || 0)
    + (estado.chatMencoes || 0)
    + (estado.avisosAgenda || 0);

  return {
    total,
    // O botão do Chat no cabeçalho lê daqui (sem pedido próprio).
    chat: { permitido: podeChat, conversas: estado.chatNaoLidas || 0, mencoes: estado.chatMencoes || 0 },
    avisos: { naoLidas: estado.avisosAgenda || 0, ultimo: estado.ultimoAviso },
    fontes: [
      {
        id: "avisos_agenda",
        label: "Agenda: tarefas compartilhadas, comentários e lembretes",
        count: estado.avisosAgenda || 0,
        url: "/minhas-pendencias?avisos=1",
        // Como as filas de solicitação: só aparece quando há aviso (zerado é ruído).
        permitido: Boolean(authenticated) && (estado.avisosAgenda || 0) > 0,
      },
      {
        id: "chat_mencoes",
        label: "Menções a você no Chat",
        count: estado.chatMencoes || 0,
        url: "/chat",
        permitido: podeChat,
      },
      {
        id: "chat",
        label: "Conversas do Chat com mensagem não lida",
        count: estado.chatNaoLidas || 0,
        url: "/chat",
        permitido: podeChat,
      },
      {
        id: "whatsapp",
        label: "Mensagens WhatsApp não lidas",
        count: whatsapp?.total || 0,
        url: "/whatsapp/inbox",
        permitido: hasPermission("whatsapp:atender"),
      },
      {
        id: "assistencias",
        // Pós-venda (chamado do CLIENTE) — não confundir com as solicitações
        // internas de assistência (ASM/AST/ASP), que têm fontes próprias.
        label: "Chamados de clientes (pós-venda) atrasados",
        count: estado.assistencias,
        url: "/posvenda/assistencias?atrasadas=true",
        permitido: podeAssistencia,
      },
      {
        id: "contratos",
        label: "Contratos aguardando assinatura",
        count: estado.contratos,
        // M-PED-3: `/comercial/contratos` nunca existiu — o link caía no
        // catch-all e jogava a pessoa no dashboard. O contrato pendente
        // vem de orçamento aprovado; é lá que ele se resolve.
        url: "/orcamentos?situacao=APROVADO",
        permitido: podeContrato,
      },
      {
        id: "oses",
        label: "OSEs atrasadas",
        count: estado.oses,
        url: "/terceirizacao/oses?atrasadas=true",
        permitido: podeOse,
      },
      {
        id: "revisoes_orcamento",
        label: "Orçamentos aguardando revisão",
        count: estado.revisoesOrcamento,
        // REF-15: leva para a lista de pendencias do usuario (consolidada).
        url: "/minhas-pendencias",
        permitido: podeRevisaoOrc,
      },
      {
        id: "followups",
        label: "Follow-ups de lead vencidos",
        count: estado.followups,
        url: "/minhas-pendencias",
        permitido: podeLead,
      },
      {
        id: "vendas_sem_ganho",
        label: "Vendas sem ganho no CRM",
        count: estado.vendasSemGanho,
        // Cada item em Minhas Pendências abre o cockpit do lead (rotaOrigem).
        url: "/minhas-pendencias",
        permitido: podeLead,
      },
      {
        id: "leads_sem_contato",
        label: "Leads novos sem contato",
        count: estado.leadsSemContato,
        // Cada item em Minhas Pendências abre o cockpit do lead (rotaOrigem).
        url: "/minhas-pendencias",
        permitido: podeLead,
      },
      {
        id: "tarefas_lead",
        label: "Tarefas de lead vencidas",
        count: estado.tarefasLead,
        url: "/minhas-pendencias",
        permitido: podeLead,
      },
      {
        id: "compromissos_lead",
        label: "Compromissos de lead nas próximas 24 h",
        count: estado.compromissosLead,
        url: "/minhas-pendencias",
        permitido: podeLead,
      },
      {
        id: "nf_erro",
        label: "NF-e recusadas",
        count: estado.nfErro,
        url: "/fiscal/notas-fiscais?situacao=ERRO",
        permitido: podeFiscal,
      },
      {
        id: "cobrancas_vencidas",
        label: "Cobranças vencidas",
        count: estado.cobrancasVencidas,
        url: "/cobranca/boletos",
        permitido: podeCobranca,
      },
      {
        id: "ajustes_aguardando",
        label: "Ajustes de estoque aguardando aprovação",
        count: estado.ajustesAguardando,
        url: "/suprimentos/movimentos?aba=aprovacao",
        permitido: podeAprovarAjuste,
      },
      {
        id: "inventarios_aguardando",
        label: "Inventários aguardando aprovação",
        count: estado.inventariosAguardando,
        url: "/suprimentos/inventario",
        permitido: podeAprovarInventario,
      },
      {
        id: "solicitacoes_cadastro",
        label: "Materiais a cadastrar",
        count: estado.solicitacoesCadastro,
        url: "/suprimentos/materiais-a-cadastrar",
        permitido: podeCadastrarSolicitado,
      },
      {
        id: "sobras_previstas",
        label: "Sobras previstas para confirmar",
        count: estado.sobrasPrevistas,
        url: "/suprimentos/sobras",
        permitido: podeConfirmarSobra,
      },
      {
        id: "requisicoes_atrasadas",
        label: "Requisições de compra atrasadas",
        count: estado.requisicoesAtrasadas,
        url: "/suprimentos/requisicoes",
        permitido: podeCotar,
      },
      {
        id: "formularios_pendentes",
        label: "Formulários: respostas que não viraram lead ou envios recusados",
        count: estado.formulariosPendentes,
        // Cada item em Minhas Pendências nomeia o formulário e a causa.
        url: "/minhas-pendencias",
        permitido: podeGerirFormularios,
      },
      {
        id: "mapa_producao",
        label: "Mapa da Produção: tarefas e peças atrasadas ou pausadas",
        count: estado.mapaProducao,
        // Cada item em Minhas Pendências abre o setor da tarefa ou da peça.
        url: "/minhas-pendencias",
        permitido: podeOperarMapa,
      },
      {
        id: "executivo_recebidos",
        label: "Executivo: ambientes que você recebeu para projetar",
        count: estado.executivoRecebidos,
        url: "/executivo",
        permitido: podeSerProjetista,
      },
      {
        id: "central_pendencias",
        label: "Central de Projetos: pendências esperando a equipe",
        count: estado.centralPendencias || 0,
        url: "/central-projetos/pendencias",
        permitido: podeVerCentral && hasPermission("central_projetos:tratar"),
      },
      {
        id: "central_alerta_projetos",
        label: "Central de Projetos: conversas com mensagem para a equipe de projetos",
        count: estado.centralAlertaProjetos || 0,
        url: "/central-projetos/conversas?alerta=projetos",
        permitido: podeVerCentral,
      },
      {
        id: "central_monitor",
        label: "Central de Projetos: monitor do WhatsApp parado ou pedindo QR",
        count: estado.centralMonitorParado || 0,
        url: "/central-projetos/configuracao",
        permitido: podeVerCentral && hasPermission("central_projetos:configurar"),
      },
      {
        id: "executivo_fora_de_ordem",
        label: "Revisão: ambientes aprovados antes da revisão (fora de ordem)",
        count: foraDeOrdemExecutivo,
        url: "/revisao",
        permitido: podeRevisar,
      },
      {
        id: "vendas_assinadas",
        label: "Vendas assinadas aguardando ligação na planilha",
        count: estado.vendasAssinadas || 0,
        url: "/legado/capacidade?aba=vendas",
        permitido: podeLigarVenda,
      },
      {
        id: "area_pcp_paradas",
        label: "Execuções do PCP paradas há mais de 1 dia útil",
        count: estado.areaPcpParadas || 0,
        url: "/legado/area-pcp/em-andamento",
        permitido: podeExecutarPcp,
      },
      ...FILAS_SOLICITACOES.map((f) => ({
        id: `solicitacoes_${f.id}`,
        label: estado.solicitacoes?.rotulos?.[f.id] || f.id,
        count: estado.solicitacoes?.[f.id] || 0,
        url: f.url,
        // Sete filas zeradas seriam ruído no menu de quem só pede (montador):
        // cada fila de solicitação só aparece quando tem algo.
        permitido: podeSolicitacoes && (estado.solicitacoes?.[f.id] || 0) > 0,
      })),
    ],
    atualizadoEm: estado.atualizadoEm,
    atualizar: carregar,
  };
}


// Fase 6 (perf): Provider UNICO do agregador de notificacoes. Antes o hook
// rodava 2x (Header + NotificationBell, que vive dentro do Header), cada um
// com seu polling 60s => requisicoes DUPLICADAS (assistencias/contratos/oses/
// revisoes) batendo no backend. Agora 1 instancia roda no Provider e os
// consumidores leem do context. A interface publica (useNotificacoesAgregadas)
// NAO muda — Header e NotificationBell continuam iguais.
export function NotificacoesProvider({ children }) {
  const valor = _useNotificacoesAgregadasInterno();
  return React.createElement(
    NotificacoesContext.Provider, { value: valor }, children,
  );
}


const _DEFAULT_NOTIFICACOES = {
  total: 0,
  fontes: [],
  avisos: { naoLidas: 0, ultimo: null },
  atualizadoEm: null,
  atualizar: () => {},
};

export default function useNotificacoesAgregadas() {
  return useContext(NotificacoesContext) || _DEFAULT_NOTIFICACOES;
}
